import type {
  AutoTopupIneligibleReason,
  NegotiationBalance,
  NegotiationBalanceEntry,
  NegotiationBalancePreview,
  NegotiationPendingTopup,
  NegotiationTopupFee,
  NegotiationTopupLimits,
  NegotiationTopupState,
} from "../../services/negotiationBalance";

/** Polling of an open recharge QR (GET /topups/:id). */
export const RECHARGE_POLL_MS = 2_000;
/**
 * Payments still credits a receipt it proves after a QR expired or was
 * removed (expired|removed → receipt_pending), and its observer keeps asking
 * Efí until 10 min past the deadline. The modal keeps watching that long.
 */
export const LATE_RECEIPT_WINDOW_MS = 10 * 60_000;
/** Polling of an expired or removed QR while a late receipt can still arrive. */
export const LATE_RECEIPT_POLL_MS = 10_000;
/** Polling of the balance chip on /negociar and of the payments tab panel. */
export const BALANCE_POLL_MS = 15_000;
/** Bounds of the net Payments enforces when its answer does not state them. */
export const DEFAULT_MIN_TOPUP_CENTS = 1_000;
export const DEFAULT_MAX_TOPUP_CENTS = 5_000_000;

/** Whole reais needed to cover `cents`, rounded up (a R$ 0,01 gap needs R$ 1). */
export function ceilReais(cents: number): number {
  return cents <= 0 ? 0 : Math.ceil(cents / 100);
}

/**
 * The recharge prefill, in whole reais, for a shortfall: never below the
 * smallest top-up Payments accepts (a R$ 0,50 gap still needs a R$ 10
 * recharge, or "Gerar Pix" would stay disabled) and never above the largest.
 */
export function rechargePrefillReais(
  amountBRL: number | undefined,
  limits: NegotiationTopupLimits,
): number | undefined {
  if (amountBRL === undefined || !Number.isFinite(amountBRL) || amountBRL <= 0) return undefined;
  const min = Math.ceil(limits.minTopupCents / 100);
  const max = Math.floor(limits.maxTopupCents / 100);
  return Math.min(Math.max(Math.ceil(amountBRL), min), max);
}

/**
 * A whole-real amount typed by the company. Only digits (and an optional
 * ",00") are accepted: the balance a top-up asks for is always a whole BRL
 * value (the Pix total, with the fee, need not be).
 */
export function parseWholeReais(draft: string): number | null {
  const clean = draft.replace(/\s/g, "").replace(/^R\$/i, "").replace(/,00$/, "");
  if (!/^\d{1,7}$/.test(clean)) return null;
  const reais = Number(clean);
  return Number.isSafeInteger(reais) && reais > 0 ? reais : null;
}

export type TopupQuote = { netCents: number; feeCents: number; chargeCents: number };

/**
 * The gross-up of a recharge (owner decision 2026-10-30), shown before its QR
 * is generated: for the balance the company wants (`netCents`) the top-up fee
 * is max(ceil(net × bps / 10000), floorCents), the expression Payments
 * charges, and the Pix total is net + fee. The balance receives exactly the
 * net. The parameters are the ones Payments declares (`topupFee`); once a QR
 * exists, the screen shows the amounts Payments answered for it instead.
 */
export function topupQuote(netCents: number, fee: NegotiationTopupFee): TopupQuote | null {
  if (!Number.isSafeInteger(netCents) || netCents <= 0) return null;
  const feeCents = Math.max(Math.floor((netCents * fee.bps + 9_999) / 10_000), fee.floorCents);
  const chargeCents = netCents + feeCents;
  return Number.isSafeInteger(chargeCents) ? { netCents, feeCents, chargeCents } : null;
}

/** The declared fee rate in pt-BR: 200 bps reads "2%", 119 bps "1,19%". */
export function formatFeeRate(bps: number): string {
  return `${(bps / 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`;
}

const TERMINAL: ReadonlySet<NegotiationTopupState> = new Set<NegotiationTopupState>([
  "confirmed",
  "expired",
  "removed",
  "abandoned",
  "unknown",
]);

/**
 * The QR itself is over: no new payment can start on it. Expired and removed
 * are not final for the money, though: see lateReceiptDeadline.
 */
export function isTerminalTopupState(state: NegotiationTopupState): boolean {
  return TERMINAL.has(state);
}

/**
 * Until when (epoch ms) an expired or removed QR may still turn into a credit:
 * 10 min past its deadline, and never more than 10 min after the modal first
 * saw it end (a QR cancelled long before its deadline).
 */
export function lateReceiptDeadline(expiresAt: string, seenMs: number): number {
  const fromSeen = seenMs + LATE_RECEIPT_WINDOW_MS;
  const end = Date.parse(expiresAt);
  return Number.isFinite(end) ? Math.min(end + LATE_RECEIPT_WINDOW_MS, fromSeen) : fromSeen;
}

const PENDING_LABEL: Partial<Record<NegotiationTopupState, string>> = {
  requested: "gerando o Pix",
  put_unknown: "gerando o Pix",
  active: "aguardando pagamento",
  receipt_pending: "confirmando",
};

/** pt-BR state of a top-up still in transit. */
export function pendingTopupLabel(state: NegotiationTopupState): string {
  return PENDING_LABEL[state] ?? "em andamento";
}

/**
 * An immediate-Pix top-up that still counts toward maxOpenTopups and whose QR
 * can be shown again (and cancelled) from the recharge modal. A weekly line
 * has no QR.
 */
export function isResumableTopup(topup: NegotiationPendingTopup): boolean {
  return topup.channel === "cob" && PENDING_LABEL[topup.state] !== undefined;
}

/** Why a bid of this preview cannot be admitted, or null when the balance covers it. */
export type BalanceShortfall = "missing" | "restricted" | "disabled";

export function balanceShortfall(
  preview: NegotiationBalancePreview | undefined,
): BalanceShortfall | null {
  if (!preview) return null;
  if (!preview.prepaidRailEnabled) return "disabled";
  if (preview.accountState === "restricted") return "restricted";
  if (preview.missingCents > 0) return "missing";
  return null;
}

/**
 * A coarse shortfall for a turbo bid that has no quote of its own, read from
 * the last balance answer. A bid of `amountBRL` reserves at least
 * amountBRL × R$ 1 (the monopoly multiplier is never below 1), so an
 * available balance below that, or a restricted account, cannot admit it.
 * The notice's missingCents is therefore a lower bound. Null when the answer
 * cannot rule the bid out; Payments still decides every bid that goes out.
 */
export function coarseBalanceShortfall(
  snapshot: NegotiationBalance | null,
  amountBRL: number,
): NegotiationBalancePreview | null {
  if (!snapshot?.account || snapshot.rail !== "prepaid") return null;
  const { state } = snapshot.account;
  if (state !== "active" && state !== "restricted") return null;
  const requiredCents = amountBRL * 100;
  if (!Number.isSafeInteger(requiredCents) || requiredCents <= 0) return null;
  const missingCents = Math.max(requiredCents - snapshot.availableCents, 0);
  if (state === "active" && missingCents === 0) return null;
  return {
    rail: "prepaid",
    accountState: state,
    prepaidRailEnabled: true,
    availableCents: snapshot.availableCents,
    reservedCents: snapshot.reservedCents,
    reservedOnPostCents: 0,
    pendingTopupCents: snapshot.pendingTopupCents,
    requiredCents,
    missingCents,
  };
}

export const RESTRICTED_COPY =
  "Saldo de Negociações bloqueado por devolução de Pix. Recarregue para regularizar.";
export const DISABLED_COPY =
  "O Saldo de Negociações está temporariamente indisponível para lances.";
export const RESERVATION_COPY =
  "O lance reserva o valor máximo. Na apuração, são debitados os lances vencedores e os patrocínios do público menor habilitados.";

export const INELIGIBLE_COPY: Record<AutoTopupIneligibleReason, string> = {
  pix_automatico_not_approved:
    "A recarga automática exige o Pix Automático aprovado. Autorize o Pix Automático nesta página.",
  weekly_limit_missing:
    "A recarga automática exige um limite semanal fixo definido no Pix Automático.",
  bank_limit_suspected:
    "A recarga automática foi desativada porque o banco recusou um débito semanal com a recarga. Confira o limite do débito automático no banco da empresa antes de ativar de novo.",
  terms_required: "A recarga automática exige o aceite das condições do Saldo de Negociações.",
  unknown: "A recarga automática não está disponível agora.",
};

const ENTRY_REASONS: Record<string, string> = {
  pix_returned: "Pix devolvido",
  weekly_returned: "débito semanal devolvido",
};

/** pt-BR label of a ledger line; the reason is shown only when it is a known code. */
export function entryLabel(entry: NegotiationBalanceEntry): string {
  const base: Record<NegotiationBalanceEntry["kind"], string> = {
    topup_credit: "Recarga",
    consumption_debit: "Consumo do ciclo",
    refund_debit: "Reembolso",
    refund_failed_credit: "Reembolso não realizado",
    reversal_debit: "Estorno",
    unknown: "Movimentação",
  };
  const reason = entry.reason ? ENTRY_REASONS[entry.reason] : undefined;
  return reason ? `${base[entry.kind]} (${reason})` : base[entry.kind];
}

/** Civil date and time in São Paulo, or null for an unreadable instant. */
export function formatInstant(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** (h:)mm:ss until `expiresAt`, or null once it passed. */
export function countdown(expiresAt: string, nowMs: number): string | null {
  const end = Date.parse(expiresAt);
  if (!Number.isFinite(end)) return null;
  const remaining = Math.floor((end - nowMs) / 1000);
  if (remaining <= 0) return null;
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;
  const clock = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  return hours > 0 ? `${hours}:${clock}` : clock;
}
